/**
 * Phase 49 — Material-scoped pricing engines.
 *
 * A pricing engine is a small object that owns the inputs, formulas and UI
 * fragment for automatically pricing items made of a specific material. Each
 * engine is fully isolated from the others so new engines (18K GOLD, 22K GOLD,
 * etc.) can be added later without touching the Silver engine.
 *
 * Contract for every engine:
 *
 *   computePurchaseTry(inputs) → number | null      // returns TRY or null when inputs incomplete
 *   computeSaleTry(purchaseTry, multiplier) → number | null
 *   multipliers                                     // ordered list of allowed multipliers
 *   defaultMultiplier
 *   sessionKeys                                     // sessionStorage keys used for pre-fill
 *   testids                                         // stable data-testids for tests
 *
 * Registry lookup:
 *   getEngineForMaterial(material) → engine | null  // null means: no auto-pricing, fields
 *                                                    // fall back to plain manual input
 *
 * IMPORTANT: This file must not import anything from InventoryList.jsx.
 * The pricing engine is a self-contained utility.
 */

// ── Jewelry price rounding (Phase 49.2) ──────────────────────────────────
//
// Silver-shop convention: displayed prices always end with 0 or 5.
// Round to the nearest multiple of 5 (half-away rounding via Math.round).
//
//   3241 → 3240,  3243 → 3245,  3247 → 3245,  3248 → 3250,
//   8727 → 8725, 10003 → 10005
//
// Only used by engines that opt in. Manual overrides never pass through
// this function. Kept isolated so future Gold/Platinum engines can use
// their own rounding rule (e.g. round-to-10) without touching Silver.
export function roundToJewelryPrice(value) {
  const n = Number(value);
  if (!isFinite(n)) return null;
  return Math.round(n / 5) * 5;
}

// ── Silver engine ────────────────────────────────────────────────────────
//
//   Purchase (TRY) = weight (g) × USD rate (TRY per USD) × purchase USD/g
//   Sale (TRY)     = Purchase (TRY) × multiplier
//
// Applies to both 925 and 999 silver purity (same formula; the purity number
// is metadata for reporting, not part of the price).
const silverEngine = {
  id: "silver",
  label: "Gümüş Fiyatlandırma",
  computePurchaseTry({ weightGrams, usdRateTry, purchaseUsdPerGram }) {
    const w = Number(weightGrams);
    const r = Number(usdRateTry);
    const p = Number(purchaseUsdPerGram);
    if (!isFinite(w) || w <= 0) return null;
    if (!isFinite(r) || r <= 0) return null;
    if (!isFinite(p) || p <= 0) return null;
    // Phase 49.1 — jewelry business does not use decimal prices.
    // Phase 49.2 — auto-calc results always end with 0 or 5 (jewelry
    // rounding). Manual overrides bypass this function entirely.
    return roundToJewelryPrice(w * r * p);
  },
  computeSaleTry(purchaseTry, multiplier) {
    const c = Number(purchaseTry);
    const m = Number(multiplier);
    if (!isFinite(c) || c <= 0) return null;
    if (!isFinite(m) || m <= 0) return null;
    return roundToJewelryPrice(c * m);
  },
  multipliers: [1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6],
  defaultMultiplier: 3,
  sessionKeys: {
    purchaseUsd: "usm_last_silver_purchase_usd",
    multiplier: "usm_last_pricing_multiplier",
  },
  testids: {
    section: "silver-pricing-section",
    usdRate: "silver-usd-rate",
    purchaseUsd: "silver-purchase-usd",
    multiplier: "silver-multiplier",
    lockPurchase: "silver-purchase-lock",
    lockSale: "silver-sale-lock",
    resetPurchase: "silver-purchase-reset",
    resetSale: "silver-sale-reset",
  },
};

// ── Gold engine (Phase 54.22) ────────────────────────────────────────────
//
// TCMB publishes a Gold/TRY rate (e.g. 6248.61). The internal per-gram
// rate is that raw value divided by 1000 — a conversion that the backend
// applies BEFORE surfacing the number, so the engine here just reads
// `goldTryPerGram` and multiplies:
//
//   Purchase (TRY) = weight (g) × goldTryPerGram × buyMultiplier
//   Sale     (TRY) = weight (g) × goldTryPerGram × sellMultiplier
//
// The buy and sell multipliers are INDEPENDENT (unlike Silver where the
// sell multiplier is applied to the computed cost). This matches how a
// jeweller thinks about gold pricing: separate "işçilik + kâr" bumps for
// what a piece costs to acquire vs. what it sells for.
//
// Jewelry rounding (0/5 tail) is reused — the UI never surfaces the /1000
// divisor, and manual overrides bypass rounding entirely.
const goldEngine = {
  id: "gold",
  label: "Altın Fiyatlandırma",
  computePurchaseTry({ weightGrams, goldTryPerGram, buyMultiplier }) {
    const w = Number(weightGrams);
    const r = Number(goldTryPerGram);
    const m = Number(buyMultiplier);
    if (!isFinite(w) || w <= 0) return null;
    if (!isFinite(r) || r <= 0) return null;
    if (!isFinite(m) || m <= 0) return null;
    return roundToJewelryPrice(w * r * m);
  },
  computeSaleTry({ weightGrams, goldTryPerGram, sellMultiplier }) {
    const w = Number(weightGrams);
    const r = Number(goldTryPerGram);
    const m = Number(sellMultiplier);
    if (!isFinite(w) || w <= 0) return null;
    if (!isFinite(r) || r <= 0) return null;
    if (!isFinite(m) || m <= 0) return null;
    return roundToJewelryPrice(w * r * m);
  },
  // Presets an operator can chip through; free-form entry always allowed.
  multipliers: [500, 600, 650, 700, 750, 800, 850, 900, 950, 1000, 1100, 1200],
  defaultBuyMultiplier: 700,
  defaultSellMultiplier: 800,
  sessionKeys: {
    buyMultiplier: "usm_last_gold_buy_multiplier",
    sellMultiplier: "usm_last_gold_sell_multiplier",
  },
  testids: {
    section: "gold-pricing-section",
    rate: "gold-try-rate",
    buyMultiplier: "gold-buy-multiplier",
    sellMultiplier: "gold-sell-multiplier",
    lockPurchase: "gold-purchase-lock",
    lockSale: "gold-sale-lock",
    resetPurchase: "gold-purchase-reset",
    resetSale: "gold-sale-reset",
    editRate: "gold-rate-edit",
  },
};

const ENGINES_BY_MATERIAL = {
  "925 SILVER": silverEngine,
  "999 SILVER": silverEngine,
  "8K GOLD": goldEngine,
  "14K GOLD": goldEngine,
  "18K GOLD": goldEngine,
  "22K GOLD": goldEngine,
  "24K GOLD": goldEngine,
};

export function getEngineForMaterial(material) {
  return ENGINES_BY_MATERIAL[material] || null;
}
